<template>
    <InputSelect
        :datasource="datasource"
        v-model="internalValue"
        :auto-open="true"
    />
</template>

<script setup lang="ts">
import { $tm } from '@/locize';
import { type SelectLineValueType } from '@/objects';
import { RaceContestant, type ExitStatus } from '@/objects/generated';
import type { ISelectLineDatasource } from '@/objects/ISelectLineDatasource';
import { ref, watch } from 'vue';
import { InputSelect } from '@/components';

const props = defineProps<{
    modelValue: SelectLineValueType | null;

    /**
     * The datasource for the select
     */
    row: RaceContestant;
}>();

const rowvalue = ref<RaceContestant>(props.row);

/**
 * The internal value of the select
 */
const internalValue = ref<SelectLineValueType | null>(
    rowvalue.value.DNF
        ? 'DNF'
        : rowvalue.value.DNS
          ? 'DNS'
          : rowvalue.value.DSQ
            ? 'DSQ'
            : rowvalue.value.DNQ
              ? 'DNQ'
              : rowvalue.value.DNC
                ? 'DNC'
                : rowvalue.value.COMPLETED
                  ? 'Finished'
                  : rowvalue.value.LAP
                    ? 'LAP'
                    : rowvalue.value.REL
                      ? 'REL'
                      : null,
);

/**
 * The datasource for the select
 */
const datasource = ref<ISelectLineDatasource>({
    source: [
        { value: null, text: $tm('exitstatus.none') },
        { value: 'Finished', text: $tm('exitstatus.finished') },
        { value: 'DNS', text: 'DNS' },
        { value: 'DNF', text: 'DNF' },
        { value: 'DSQ', text: 'DSQ' },
        { value: 'DNQ', text: 'DNQ' },
        { value: 'DNC', text: 'DNC' },
        { value: 'LAP', text: 'LAP' },
        { value: 'REL', text: 'REL' },
    ],
});

/**
 * Reset all the flags on the row. This is to make sure only one flag is set at a time
 */
const resetAllFlags = () => {
    rowvalue.value.DNF = false;
    rowvalue.value.DNS = false;
    rowvalue.value.DSQ = false;
    rowvalue.value.DNQ = false;
    rowvalue.value.DNC = false;
    rowvalue.value.COMPLETED = false;
    rowvalue.value.LAP = false;
    rowvalue.value.REL = false;
};

/**
 * Set the flag on the row
 * @param flag The flag to set
 */
const setFlag = (flag: ExitStatus) => {
    resetAllFlags();

    // We need to check if there is a key in the rowvalue that is the same as the flag
    if (flag == 'DNC') {
        rowvalue.value.DNC = true;
    } else if (flag == 'DNQ') {
        rowvalue.value.DNQ = true;
    } else if (flag == 'DNS') {
        rowvalue.value.DNS = true;
    } else if (flag == 'DNF') {
        rowvalue.value.DNF = true;
    } else if (flag == 'DSQ') {
        rowvalue.value.DSQ = true;
    } else if (flag == 'Finished') {
        rowvalue.value.COMPLETED = true;
    } else if (flag == 'LAP') {
        rowvalue.value.LAP = true;
    } else if (flag == 'REL') {
        rowvalue.value.REL = true;
    }
};

watch(
    () => internalValue.value,
    (nv) => setFlag(nv as ExitStatus),
);
</script>
